import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Hook for subscribing to real-time updates on a Supabase table
 * @param {Object} options - Configuration options
 * @param {string} options.table - Table name to subscribe to
 * @param {string} options.event - Event type: 'INSERT' | 'UPDATE' | 'DELETE' | '*'
 * @param {string} options.filter - Optional filter (e.g., 'company_id=eq.xxx')
 * @param {Function} options.onData - Callback when data changes
 * @param {boolean} options.enabled - Whether subscription is active
 */
export const useRealtimeSubscription = ({
  table,
  event = '*',
  filter,
  onData,
  enabled = true,
}) => {
  const channelRef = useRef(null);

  useEffect(() => {
    if (!enabled || !table) return;

    const channelName = `${table}:${filter || 'all'}:${Date.now()}`;

    const subscriptionConfig = {
      event,
      schema: 'public',
      table,
    };

    if (filter) {
      subscriptionConfig.filter = filter;
    }

    channelRef.current = supabase
      .channel(channelName)
      .on('postgres_changes', subscriptionConfig, (payload) => {
        onData?.(payload);
      })
      .subscribe();

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [table, event, filter, onData, enabled]);

  const unsubscribe = useCallback(() => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, []);

  return { unsubscribe };
};

/**
 * Hook for real-time job instance updates
 * Automatically refreshes data when job instances change
 */
export const useRealtimeJobs = (companyId, onUpdate) => {
  return useRealtimeSubscription({
    table: 'job_instances',
    event: '*',
    filter: companyId ? `company_id=eq.${companyId}` : undefined,
    onData: (payload) => {
      console.log('[Realtime] Job instance update:', payload.eventType);
      onUpdate?.(payload);
    },
    enabled: !!companyId,
  });
};

/**
 * Hook for real-time timesheet updates
 * Useful for tracking clock-in/out events
 */
export const useRealtimeTimesheets = (companyId, onUpdate) => {
  return useRealtimeSubscription({
    table: 'timesheets',
    event: '*',
    filter: companyId ? `company_id=eq.${companyId}` : undefined,
    onData: (payload) => {
      console.log('[Realtime] Timesheet update:', payload.eventType);
      onUpdate?.(payload);
    },
    enabled: !!companyId,
  });
};

/**
 * Hook for real-time contractor job updates (for contractor dashboard)
 */
export const useRealtimeContractorJobs = (contractorId, onUpdate) => {
  return useRealtimeSubscription({
    table: 'job_instances',
    event: '*',
    filter: contractorId ? `contractor_id=eq.${contractorId}` : undefined,
    onData: (payload) => {
      console.log('[Realtime] Contractor job update:', payload.eventType);
      onUpdate?.(payload);
    },
    enabled: !!contractorId,
  });
};

/**
 * Hook for real-time overtime request updates
 */
export const useRealtimeOvertimeRequests = (companyId, onUpdate) => {
  return useRealtimeSubscription({
    table: 'overtime_requests',
    event: '*',
    filter: companyId ? `company_id=eq.${companyId}` : undefined,
    onData: (payload) => {
      console.log('[Realtime] Overtime request update:', payload.eventType);
      onUpdate?.(payload);
    },
    enabled: !!companyId,
  });
};

/**
 * Hook for real-time notification updates
 */
export const useRealtimeNotifications = (userId, onUpdate) => {
  return useRealtimeSubscription({
    table: 'notifications',
    event: 'INSERT',
    filter: userId ? `user_id=eq.${userId}` : undefined,
    onData: (payload) => {
      console.log('[Realtime] New notification:', payload.new);
      onUpdate?.(payload);
    },
    enabled: !!userId,
  });
};

/**
 * Combined hook for corporate dashboard real-time updates
 * Subscribes to jobs, timesheets, and overtime in one hook
 */
export const useCorporateDashboardRealtime = (companyId, onUpdate) => {
  const handleUpdate = useCallback((payload) => {
    onUpdate?.(payload);
  }, [onUpdate]);

  useRealtimeJobs(companyId, handleUpdate);
  useRealtimeTimesheets(companyId, handleUpdate);
  useRealtimeOvertimeRequests(companyId, handleUpdate);
};

/**
 * Combined hook for contractor dashboard real-time updates
 */
export const useContractorDashboardRealtime = (contractorId, userId, onUpdate) => {
  const handleUpdate = useCallback((payload) => {
    onUpdate?.(payload);
  }, [onUpdate]);

  useRealtimeContractorJobs(contractorId, handleUpdate);
  useRealtimeNotifications(userId, handleUpdate);
};

export default useRealtimeSubscription;
